import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Compra la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Argentina 2026",
    es: 'Ahora eres parte de Argentina 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Argentina,",
    es: 'No Solo Apoyes a Argentina,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: 'The Argentine national team represents more than just football; it embodies the passion, resilience, and artistry of a nation. From the historic triumph in 1978 to conquering the world in Qatar, La Albiceleste continues to write its name in the stars.',
    es: 'La selección argentina representa más que solo fútbol; encarna la pasión, la resiliencia y el arte de una nación. Desde el histórico triunfo en 1978 hasta conquistar el mundo en Qatar, La Albiceleste continúa escribiendo su nombre en las estrellas.'
  },
  'team.since': {
    en: 'Since 1902',
    es: 'Desde 1902'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: '3 World Cups',
    es: '3 Copas del Mundo'
  },
  'team.euroDesc': {
    en: '1978, 1986, 2022',
    es: '1978, 1986, 2022'
  },
  'team.nationsLeague': {
    en: '16 Copa América',
    es: '16 Copas América'
  },
  'team.nationsLeagueDesc': {
    en: 'Most recent: 2021, 2024',
    es: 'Más recientes: 2021, 2024'
  },
  'team.euros': {
    en: '2 Olympic Golds',
    es: '2 Oros Olímpicos'
  },
  'team.eurosDesc': {
    en: '2004, 2008',
    es: '2004, 2008'
  },
  'team.worldCups': {
    en: '18 World Cups',
    es: '18 Mundiales'
  },
  'team.worldCupsDesc': {
    en: 'Global Stage Appearances',
    es: 'Apariciones en el Escenario Global'
  },
  'team.videoLabel': {
    en: 'Sangre Argentina',
    es: 'Sangre Argentina'
  },
  'team.videoTitle1': {
    en: 'One More',
    es: 'Un Baile'
  },
  'team.videoTitle2': {
    en: 'Dance',
    es: 'Más'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'El Plantel'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Argentina',
    es: 'Argentina'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantel Completo en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Mediocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensor'
  },
  'players.goalkeeper': {
    en: 'Goalkeeper',
    es: 'Arquero'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow La Albiceleste in Group A. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue a La Albiceleste en el Grupo A. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Posiciones del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Posiciones en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Argentina By The Numbers',
    es: 'Argentina en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Apariciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.ronaldo': {
    en: 'Messi Goals (Record)',
    es: 'Goles de Messi (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.euro2016': {
    en: '2022 World Cup Final',
    es: 'Final del Mundial 2022'
  },
  'history.eder': {
    en: "Maradona's Top 5 World Cup Goals",
    es: 'Top 5 Goles de Maradona en Mundiales'
  },
  'history.ronaldoGoals': {
    en: 'Lionel Messi - All FIFA World Cup Goals and Assists',
    es: 'Lionel Messi - Todos los Goles y Asistencias en Mundiales FIFA'
  },
  'history.nationsLeague': {
    en: 'Copa América 2024 Final',
    es: 'Final de la Copa América 2024'
  },
  'history.kempes': {
    en: '1978 World Cup Final',
    es: 'Final del Mundial 1978'
  },
  'history.copa2021': {
    en: 'Copa América 2021 Final',
    es: 'Final de la Copa América 2021'
  },
  'history.dimaria': {
    en: 'Di María All Goals in Finals',
    es: 'Todos los Goles de Di María en Finales'
  },
  'history.messiGoals': {
    en: "Emi Martinez's Last Minute Save vs France",
    es: 'La Atajada de Último Minuto de Emi Martínez vs Francia'
  },
  'history.argentina1986': {
    en: "All of Argentina's 1986 World Cup Goals",
    es: 'Todos los Goles de Argentina en el Mundial 1986'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Argentina',
    es: 'Argentina'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Argentina fans on tickets, hotels, flights & transport to support La Albiceleste in North America.',
    es: 'Descuentos exclusivos para fans de Argentina en entradas, hoteles, vuelos y transporte para apoyar a La Albiceleste en Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Encontrar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Buenos Aires & Córdoba to USA',
    es: 'Tarifas especiales desde Buenos Aires y Córdoba a EE.UU.'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de traslado y alquiler de autos en ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Argentina 2026',
    es: 'Todo lo Que Necesitas para Argentina 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Cuartel del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Hincha'
  },
  'fanHq.countdown': {
    en: "Countdown to Argentina's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de Argentina'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito de la Hinchada'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de streaming y TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Rankings mundiales oficiales'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Argentina 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta inteligente NFC para Argentina 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje al alcance de tu mano.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Posiciones'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}